import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { business } from "../mock";

const links = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/#services" },
  { label: "Work", to: "/#work" },
  { label: "Reviews", to: "/#reviews" },
  { label: "Availability", to: "/#availability" },
  { label: "Why Us", to: "/#about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  // Over the dark hero image = light text; otherwise solid bar with dark text
  const overHero = pathname === "/" && !scrolled && !open;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        overHero
          ? "bg-transparent py-5"
          : "bg-brand-cream/90 backdrop-blur-md border-b border-black/5 py-3 shadow-sm shadow-black/5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <span className={`flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-lg transition-colors duration-500 ${overHero ? "bg-white/10 backdrop-blur-sm" : "bg-brand-ink"}`}>
            <Logo className="h-7 w-7 sm:h-8 sm:w-8 transition-transform duration-500 group-hover:rotate-6" dark />
          </span>
          <span className="font-display leading-tight tracking-tight">
            <span className={`block text-[19px] sm:text-[23px] font-semibold transition-colors duration-500 ${overHero ? "text-white" : "text-brand-ink"}`}>Max&rsquo;s Concrete Cutting</span>
            <span className="block text-[11px] sm:text-[13px] tracking-[0.24em] uppercase text-brand-teal font-semibold">Perth</span>
          </span>
        </Link>

        <nav className="hidden md:flex flex-1 items-center justify-center gap-9">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.to}
              className={`link-sweep text-sm font-medium transition-colors duration-300 ${overHero ? "text-white/85 hover:text-white" : "text-brand-ink/75 hover:text-brand-ink"}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          <a
            href={business.phoneHref}
            className={`flex items-center gap-2 text-sm font-semibold transition-colors ${overHero ? "text-white hover:text-brand-tealsoft" : "text-brand-ink hover:text-brand-teal"}`}
          >
            <Phone className="h-4 w-4" strokeWidth={2.2} />
            {business.phone}
          </a>
          <Link
            to="/quote"
            className="inline-flex items-center rounded-full bg-brand-teal px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-brand-tealdark hover:shadow-lg hover:shadow-brand-teal/25"
          >
            Get a Quote
          </Link>
        </div>

        <button
          className={`md:hidden inline-flex items-center justify-center h-14 w-14 -mr-2 rounded-lg ${overHero ? "text-white" : "text-brand-ink"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X className="h-9 w-9" strokeWidth={2.4} /> : <Menu className="h-9 w-9" strokeWidth={2.4} />}
        </button>
      </div>

      {/* Mobile sheet */}
      <div className={`md:hidden overflow-hidden transition-all duration-500 ease-out ${open ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="px-5 pt-3 pb-6 flex flex-col gap-1 bg-brand-cream">
          {links.map((l) => (
            <a key={l.label} href={l.to} onClick={() => setOpen(false)} className="py-3 text-lg font-display font-medium border-b border-black/5 text-brand-ink">
              {l.label}
            </a>
          ))}
          <a href={business.phoneHref} onClick={() => setOpen(false)} className="py-3 text-lg font-display font-medium flex items-center gap-2 text-brand-ink">
            <Phone className="h-5 w-5 text-brand-teal" /> {business.phone}
          </a>
          <Link to="/quote" onClick={() => setOpen(false)} className="mt-3 inline-flex items-center justify-center rounded-full bg-brand-teal px-5 py-3 text-base font-semibold text-white">
            Get a Quote
          </Link>
        </div>
      </div>
    </header>
  );
}
